/**
 * Who the signed-in person is, as the worker knows them.
 *
 * Google gives us a name and a photo at login, but only the tab that handled the
 * callback ever saw them: a person who logged in on their phone, or before this
 * existed, still shows up here as the front of their email address. The worker now
 * keeps `{email}/identity.json` from that login, and this reads it back so any
 * session can recover the name and the picture without a fresh Google round-trip.
 *
 * Everything here fails soft. Identity is decoration on top of a session that is
 * already valid — a worker outage or an expired token must never break a page, so a
 * failure resolves to null and the caller keeps whatever it had.
 *
 * Deliberately a PLAIN fetch, not authedFetch: authedFetch heals a 401 by throwing
 * the stored r2 token away and minting a new one. A decorative read that runs at
 * boot must never be the thing that deletes a working token.
 */

import { getSessionToken, R2_WORKER_URL } from "@/features/bucket/api";

export interface AccountIdentity {
    email: string;
    /** Real name if we have one — never a placeholder like "Select name". */
    name: string | null;
    /** Where the name came from: the person's own profile, or Google. */
    nameSource: "profile" | "google" | null;
    hasPhoto: boolean;
    /** Worker path, needs a Bearer header — not usable as an <img src>. */
    photoUrl: string | null;
    /** Google's own CDN URL: no auth, usable as an <img src> directly. */
    googlePicture: string | null;
}

let cache: AccountIdentity | null = null;
let inflight: Promise<AccountIdentity | null> | null = null;
let photoUrlCache: string | null = null;
let photoInflight: Promise<string | null> | null = null;

/**
 * The session the cache belongs to. A module-level cache outlives a login: if it
 * were keyed on nothing but the page's lifetime, whoever signs in next in the
 * same tab would inherit the previous person's name and face.
 */
let cachedForEmail: string | null = null;
/**
 * Bumped on every reset. An answer that was requested under one session must
 * not land in the cache after a reset — the closures compare this before
 * assigning, so a late reply from the old person is simply dropped.
 */
let generation = 0;

const currentEmail = (): string => {
    try {
        return localStorage.getItem("site_user_email") || "";
    } catch {
        return "";
    }
};

const bearer = (): RequestInit => ({
    headers: { Authorization: `Bearer ${getSessionToken()}` },
});

/**
 * Forget everything cached. Called when a NEW session is established in the
 * same tab (login, logout, leaving impersonation). In-flight requests are
 * detached too: their reply belongs to the old session and must not be served
 * to the new one. The blob URL is only dropped, not revoked — the <img> that is
 * showing it revokes it itself when it goes away.
 */
export const resetIdentityCache = (): void => {
    generation += 1;
    cache = null;
    inflight = null;
    photoUrlCache = null;
    photoInflight = null;
    cachedForEmail = null;
};

/** Drop the cache if the session it was captured under is no longer the current one. */
const dropIfStale = (): void => {
    const email = currentEmail();
    if (cachedForEmail !== null && cachedForEmail !== email) resetIdentityCache();
};

/**
 * Cached for the life of the session: identity changes only at login, and several
 * unrelated places (boot hydration, the profile page, the header) ask for it at
 * once. The in-flight promise is shared so those become one request.
 */
export const getAccountIdentity = (): Promise<AccountIdentity | null> => {
    dropIfStale();
    if (cache) return Promise.resolve(cache);
    if (inflight) return inflight;
    // No session — nothing to ask with, and nothing to hydrate from.
    if (!getSessionToken()) return Promise.resolve(null);

    const gen = generation;
    const email = currentEmail();
    // Declared before the closure so the finally can tell "still mine" from a
    // request that a reset has already replaced.
    let p: Promise<AccountIdentity | null> | null = null;
    p = (async () => {
        try {
            const res = await fetch(`${R2_WORKER_URL}/account/identity`, bearer());
            // 401 means there is no usable session yet — not an error worth
            // surfacing, the caller simply has nothing to hydrate from.
            if (!res.ok) return null;
            const d = (await res.json()) as Partial<AccountIdentity> & {
                success?: boolean;
            };
            if (!d?.success) return null;
            const identity: AccountIdentity = {
                email: d.email || "",
                name: d.name || null,
                nameSource: d.nameSource || null,
                hasPhoto: !!d.hasPhoto,
                photoUrl: d.photoUrl || null,
                googlePicture: d.googlePicture || null,
            };
            // A reset happened while we waited: this answer is the old person's.
            if (gen !== generation) return null;
            cache = identity;
            cachedForEmail = email;
            return identity;
        } catch {
            return null;
        } finally {
            if (inflight === p) inflight = null;
        }
    })();
    inflight = p;
    return p;
};

/**
 * A URL an <img> can actually use for the person's photo.
 *
 * /account/photo wants a Bearer header, and an <img> cannot send one — so the
 * bytes are fetched here and handed over as a blob URL. If that fetch fails we
 * fall back to Google's own picture URL, which needs no auth at all; if there is
 * neither, null, and the caller keeps its initial-letter placeholder.
 *
 * The blob URL is the consumer's to revoke: see releaseAccountPhotoUrl.
 */
export const getAccountPhotoUrl = (): Promise<string | null> => {
    dropIfStale();
    if (photoUrlCache) return Promise.resolve(photoUrlCache);
    if (photoInflight) return photoInflight;

    const gen = generation;
    let p: Promise<string | null> | null = null;
    p = (async () => {
        try {
            const identity = await getAccountIdentity();
            if (!identity || gen !== generation) return null;
            let url: string | null = null;
            if (identity.hasPhoto) {
                try {
                    const res = await fetch(`${R2_WORKER_URL}/account/photo`, bearer());
                    if (res.ok) {
                        const blob = await res.blob();
                        if (blob.size > 0) url = URL.createObjectURL(blob);
                    }
                } catch {
                    // fall through to the Google URL below
                }
            }
            if (!url) url = identity.googlePicture || null;
            if (gen !== generation) {
                // Old session's answer; nobody will ever show it, so free it now.
                if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
                return null;
            }
            photoUrlCache = url;
            return url;
        } finally {
            if (photoInflight === p) photoInflight = null;
        }
    })();
    photoInflight = p;
    return p;
};

/**
 * Called by the <img>'s owner when it unmounts. A blob URL holds the bytes for
 * as long as it exists, so the consumer that stops showing it is the one that
 * knows it can go; the cache forgets it so the next visitor fetches a live one.
 */
export const releaseAccountPhotoUrl = (url: string | null | undefined): void => {
    if (!url || !url.startsWith("blob:")) return;
    if (photoUrlCache === url) photoUrlCache = null;
    URL.revokeObjectURL(url);
};
